import { Button, Flex, Group, Stack } from '@mantine/core';
import {
	IconCancel,
	IconCheck,
	IconEdit,
	IconPassword,
	IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteModal } from '../../components/DeleteModal';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { LIGHT_GREY } from '../../const/constants';
import { useModal } from '../../hooks/useModal';
import { useResponsiveSpace } from '../../hooks/useResponsiveSpace';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { mutate } from '../../util/api/mutate';
import { query } from '../../util/api/query';
import { EditStaffMemberForm } from './components/EditStaffMemberForm';
import { ResetPinModal } from './components/ResetPinModal';
import {
	StaffAddress,
	StaffAttendance,
	StaffEmergencyContact,
	StaffGeneralInfo,
} from './components/SingleStaffMemberTiles';
import type { FullOperatorDto } from './types/OperatorDto';

export function SingleStaffMember() {
	const { id, organisation } = useParams();
	const navigate = useNavigate();

	const staffMemberQuery = query<FullOperatorDto>(
		`${organisation}/operators/id/${id}`,
	);

	const deleteMutation = mutate(`${organisation}/operators/delete/${id}`);

	const [showEditOptions, setShowEditOptions] = useState(false);

	useEffect(() => {
		document.body.style.backgroundColor = LIGHT_GREY;
	}, []);

	const updateValuesInView = () => {
		setShowEditOptions(false);
		staffMemberQuery.remove();
		staffMemberQuery.remove();
	};

	const passwordModal = useModal();
	const deleteModal = useModal();

	const responsiveTableHeight = useResponsiveSpace([
		[753, '33vh'],
		[1048, '50vh'],
	]);

	return (
		<>
			{!staffMemberQuery.isLoading && staffMemberQuery.data && (
				<>
					<ResetPinModal
						opened={passwordModal.isOpen}
						close={passwordModal.close}
						username={staffMemberQuery.data.value.username}
						accountId={staffMemberQuery.data.value.id}
					/>
					<DeleteModal
						isOpened={deleteModal.isOpen}
						close={deleteModal.close}
						titleType={'Staff Member'}
						itemDescription={staffMemberQuery.data.value.username}
						mutation={deleteMutation}
						onComplete={() => {
							navigate(`/${organisation}/staff/list`);
						}}
					/>
				</>
			)}
			<PageHeader
				title={staffMemberQuery.data?.value.username ?? 'Username'}
				hideFiltering
			>
				{showEditOptions ? (
					<>
						<Group>
							<Button
								color={'medepos.1'}
								variant={'filled'}
								leftSection={<IconCancel />}
								onClick={() => setShowEditOptions(false)}
							>
								Cancel
							</Button>
							<Button
								color={'medepos.1'}
								variant={'filled'}
								leftSection={<IconCheck />}
								type={'submit'}
								form="edit-staff-form"
								data-cy="save-button"
							>
								Save Changes
							</Button>
						</Group>
					</>
				) : (
					<Group>
						<Button
							variant={'filled'}
							color={'medepos.1'}
							autoContrast
							px={'xs'}
							onClick={() => setShowEditOptions(true)}
							loading={staffMemberQuery.isLoading}
						>
							<IconEdit {...defaultIconStyle} />
						</Button>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							px={'xs'}
							onClick={() => passwordModal.open()}
							loading={staffMemberQuery.isLoading}
						>
							<IconPassword {...defaultIconStyle} />
						</Button>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							px={'xs'}
							onClick={() => deleteModal.open()}
							loading={staffMemberQuery.isLoading}
						>
							<IconTrash {...defaultIconStyle} />
						</Button>
					</Group>
				)}
			</PageHeader>
			{showEditOptions && staffMemberQuery.data?.value ? (
				<EditStaffMemberForm
					originalOperator={staffMemberQuery.data.value}
					onEditComplete={() => updateValuesInView()}
				/>
			) : (
				<>
					<Flex w={'100%'} h={'100%'} dir={'column'} gap={20}>
						<Stack w={'50%'}>
							<Paper title={'general info'}>
								<StaffGeneralInfo
									isLoading={staffMemberQuery.isLoading}
									operator={staffMemberQuery.data?.value}
								/>
							</Paper>
						</Stack>
						<Stack w={'50%'} justify="space-between">
							<Paper title={'address'}>
								<StaffAddress
									isLoading={staffMemberQuery.isLoading}
									operator={staffMemberQuery.data?.value}
								/>
							</Paper>

							<Paper title={'emergency contact'}>
								<StaffEmergencyContact
									isLoading={staffMemberQuery.isLoading}
									operator={staffMemberQuery.data?.value}
								/>
							</Paper>
						</Stack>
					</Flex>
					<Paper
						title={
							<>
								ATTENDANCE <i>{'(Past 7 Days)'}</i>
							</>
						}
						h={responsiveTableHeight}
					>
						<StaffAttendance />
					</Paper>
				</>
			)}
		</>
	);
}
